/**
 * Junto Frontend IsolatedPurchasePopup Component
 * 
 * Handles isolated purchases (like direct class booking purchases) 
 * that should not inherit from the main cart state.
 * Theme-agnostic and reusable across different studio brands.
 */

import { useState } from 'react';
import styled from 'styled-components';
import { solRoomTheme } from '../../themes/sol-room.js';
import { useAuth } from '../../contexts/ClerkAuthContext';
import { Modal } from '../ui/index.js';

const PopupTitle = styled.h3`
  font-family: ${props => props.theme?.typography?.primary?.fontFamily || solRoomTheme.typography.primary.fontFamily};
  font-size: 1.5rem;
  font-weight: 600;
  color: ${props => props.theme?.colors?.primary?.text || solRoomTheme.colors.primary.text};
  margin: 0 0 ${props => props.theme?.spacing?.lg || solRoomTheme.spacing.lg} 0;
  text-align: center;
`;

const CartContent = styled.div`
  max-height: 60vh;
  overflow-y: auto;
`;

const CartItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${props => props.theme?.spacing?.md || solRoomTheme.spacing.md};
  border-bottom: 1px solid ${props => props.theme?.colors?.neutral?.light || solRoomTheme.colors.neutral.light};
  
  &:last-child {
    border-bottom: none;
  }
`;

const ItemInfo = styled.div`
  flex: 1;
`;

const ItemName = styled.h4`
  font-family: ${props => props.theme?.typography?.primary?.fontFamily || solRoomTheme.typography.primary.fontFamily};
  font-size: 1rem;
  font-weight: 600;
  color: ${props => props.theme?.colors?.primary?.text || solRoomTheme.colors.primary.text};
  margin: 0 0 ${props => props.theme?.spacing?.xs || solRoomTheme.spacing.xs} 0;
`;

const ItemDescription = styled.p`
  font-family: ${props => props.theme?.typography?.secondary?.fontFamily || solRoomTheme.typography.secondary.fontFamily};
  font-size: 0.9rem;
  color: ${props => props.theme?.colors?.primary?.text || solRoomTheme.colors.primary.text};
  opacity: 0.7;
  margin: 0;
`;

const ItemPrice = styled.div`
  font-family: ${props => props.theme?.typography?.primary?.fontFamily || solRoomTheme.typography.primary.fontFamily};
  font-size: 1.1rem;
  font-weight: 600;
  color: ${props => props.theme?.colors?.primary?.text || solRoomTheme.colors.primary.text};
`;

const TotalSection = styled.div`
  background: ${props => props.theme?.colors?.neutral?.lightest || solRoomTheme.colors.neutral.lightest};
  border: 1px solid ${props => props.theme?.colors?.neutral?.light || solRoomTheme.colors.neutral.light};
  border-radius: 8px;
  padding: ${props => props.theme?.spacing?.lg || solRoomTheme.spacing.lg};
  margin: ${props => props.theme?.spacing?.lg || solRoomTheme.spacing.lg} 0;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme?.spacing?.sm || solRoomTheme.spacing.sm};
  
  &:last-child {
    margin-bottom: 0;
    padding-top: ${props => props.theme?.spacing?.sm || solRoomTheme.spacing.sm};
    border-top: 1px solid ${props => props.theme?.colors?.neutral?.medium || solRoomTheme.colors.neutral.medium};
    font-weight: 600;
    font-size: 1.1rem;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: ${props => props.theme?.spacing?.md || solRoomTheme.spacing.md};
  justify-content: center;
`;

const ActionButton = styled.button`
  font-family: ${props => props.theme?.typography?.button?.fontFamily || solRoomTheme.typography.button.fontFamily};
  font-size: ${props => props.theme?.typography?.button?.fontSize || solRoomTheme.typography.button.fontSize};
  font-weight: ${props => props.theme?.typography?.button?.fontWeight || solRoomTheme.typography.button.fontWeight};
  letter-spacing: ${props => props.theme?.typography?.button?.letterSpacing || solRoomTheme.typography.button.letterSpacing};
  text-transform: ${props => props.theme?.typography?.button?.textTransform || solRoomTheme.typography.button.textTransform};
  
  background: ${props => props.$primary ? (props.theme?.colors?.primary?.buttons || solRoomTheme.colors.primary.buttons) : 'transparent'};
  color: ${props => props.$primary ? (props.theme?.colors?.primary?.background || solRoomTheme.colors.primary.background) : (props.theme?.colors?.primary?.buttons || solRoomTheme.colors.primary.buttons)};
  border: 1px solid ${props => props.theme?.colors?.primary?.buttons || solRoomTheme.colors.primary.buttons};
  border-radius: 50px;
  
  padding: ${props => props.theme?.spacing?.sm || solRoomTheme.spacing.sm} ${props => props.theme?.spacing?.lg || solRoomTheme.spacing.lg};
  cursor: pointer;
  transition: ${props => props.theme?.transitions?.default || solRoomTheme.transitions.default};
  
  &:hover {
    background: ${props => props.theme?.colors?.primary?.buttons || solRoomTheme.colors.primary.buttons};
    color: ${props => props.theme?.colors?.primary?.background || solRoomTheme.colors.primary.background};
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: ${props => props.$primary ? (props.theme?.colors?.neutral?.medium || solRoomTheme.colors.neutral.medium) : 'transparent'};
    color: ${props => props.$primary ? (props.theme?.colors?.primary?.background || solRoomTheme.colors.primary.background) : (props.theme?.colors?.neutral?.medium || solRoomTheme.colors.neutral.medium)};
    border-color: ${props => props.theme?.colors?.neutral?.medium || solRoomTheme.colors.neutral.medium};
    
    &:hover {
      background: ${props => props.$primary ? (props.theme?.colors?.neutral?.medium || solRoomTheme.colors.neutral.medium) : 'transparent'};
      color: ${props => props.$primary ? (props.theme?.colors?.primary?.background || solRoomTheme.colors.primary.background) : (props.theme?.colors?.neutral?.medium || solRoomTheme.colors.neutral.medium)};
      transform: none;
    }
  }
`;

const DescriptionText = styled.p`
  font-family: ${props => props.theme?.typography?.secondary?.fontFamily || solRoomTheme.typography.secondary.fontFamily};
  font-size: 0.9rem;
  color: ${props => props.theme?.colors?.primary?.text || solRoomTheme.colors.primary.text};
  opacity: 0.8;
  text-align: center;
  margin: 0 0 ${props => props.theme?.spacing?.lg || solRoomTheme.spacing.lg} 0;
  line-height: 1.5;
`;

const IsolatedPurchasePopup = ({ 
  isOpen, 
  onClose, 
  item,
  onComplete,
  className,
  title = "Complete Purchase",
  description = "This purchase will automatically reserve your class upon completion.",
  theme = solRoomTheme 
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const { isAuthenticated } = useAuth();

  const handleCheckout = async () => {
    if (!item || !isAuthenticated) return;
    
    setIsProcessing(true);
    
    try {
      // Simulate checkout process - in real implementation this would call payment API
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Call completion handler which will trigger class booking
      if (onComplete) {
        onComplete();
      }
      
      onClose();
    } catch (error) {
      console.error('Checkout failed:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  if (!item) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="500px" theme={theme}>
      <PopupTitle theme={theme}>{title}</PopupTitle>
      
      {description && (
        <DescriptionText theme={theme}>
          {description}
        </DescriptionText>
      )}
      
      <CartContent>
        <CartItem theme={theme}>
          <ItemInfo>
            <ItemName theme={theme}>{item.name}</ItemName>
            <ItemDescription theme={theme}>
              Credit package for class booking • Quantity: {item.quantity}
            </ItemDescription>
          </ItemInfo>
          <ItemPrice theme={theme}>
            ${(item.price * item.quantity).toFixed(2)}
          </ItemPrice>
        </CartItem>
      </CartContent>

      <TotalSection theme={theme}>
        <TotalRow theme={theme}>
          <span>Subtotal:</span>
          <span>${(item.price * item.quantity).toFixed(2)}</span>
        </TotalRow>
        <TotalRow theme={theme}>
          <span>Total:</span>
          <span>${(item.price * item.quantity).toFixed(2)}</span>
        </TotalRow>
      </TotalSection>

      <ButtonContainer theme={theme}>
        <ActionButton onClick={onClose} theme={theme} disabled={isProcessing}>
          Cancel
        </ActionButton>
        <ActionButton 
          $primary 
          onClick={handleCheckout} 
          disabled={!isAuthenticated || isProcessing}
          theme={theme}
        >
          {isProcessing ? 'Processing...' : 'Purchase + Reserve'}
        </ActionButton>
      </ButtonContainer>
    </Modal>
  );
};

export default IsolatedPurchasePopup;